import { motion } from "framer-motion";
import { ShieldCheck, MapPin, KeyRound, Sparkles, Boxes, PenLine } from "lucide-react";
import { Reveal, ChapterTag } from "./Reveal";

const ADVANTAGES = [
  { icon: ShieldCheck, label: "Seguridad y videovigilancia", desc: "Tus cosas, vigiladas y protegidas." },
  { icon: MapPin, label: "En pleno Guadalajara", desc: "Calle Perú, 2 — cerca de tu casa o tu negocio." },
  { icon: KeyRound, label: "Fácil acceso", desc: "Comodidad y flexibilidad para entrar a tu trastero." },
  { icon: Sparkles, label: "Instalaciones modernas", desc: "Limpias, nuevas y bien iluminadas." },
  { icon: Boxes, label: "Diferentes tamaños", desc: "De 1 m² a grandes espacios para empresas." },
  { icon: PenLine, label: "Contratación sencilla", desc: "Sin letra pequeña ni papeleo eterno." },
];

export const BrandSection = () => (
  <section id="ventajas" data-testid="brand-section" className="py-16 sm:py-20 bg-white border-y border-brand-ink/8">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <ChapterTag num="02" label="Ventajas" />
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mt-5">
          <h2 className="font-display font-black tracking-tight text-brand-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            Tu{" "}
            <span className="relative inline-block">
              <span className="relative z-10">OtraRoom</span>
              <motion.span
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-x-0 bottom-1 h-[0.35em] bg-brand-yellow origin-left -z-0"
              />
            </span>
            .
          </h2>
          <p className="text-sm sm:text-base text-brand-ink/60 max-w-md leading-relaxed">
            <span className="font-display font-bold text-brand-ink/85">Esa habitación que te falta en casa. </span>
            Bicis, ropa de temporada, muebles, herramientas, archivos... gana espacio sin renunciar a tus cosas.
          </p>
        </div>
        <div className="mt-6 flex flex-wrap gap-2" data-testid="needs-chips">
          {["Mudanzas y reformas", "Falta de espacio en casa", "Material de autónomos", "Archivo y stock para empresas", "Equipamiento deportivo y de temporada"].map((need) => (
            <span
              key={need}
              className="inline-flex items-center gap-2 text-xs font-semibold text-brand-ink/75 bg-brand-smoke border border-brand-ink/8 rounded-full px-3.5 py-1.5"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-brand-yellow border border-brand-ink/20" />
              {need}
            </span>
          ))}
        </div>
      </Reveal>

      <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-7">
        {ADVANTAGES.map((adv, i) => (
          <motion.div
            key={adv.label}
            data-testid={`advantage-${adv.label.toLowerCase().replace(/\s+/g, "-")}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, delay: (i % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="flex gap-3.5 group"
          >
            <div className="shrink-0 w-11 h-11 rounded-xl bg-brand-yellow flex items-center justify-center group-hover:rotate-6 transition-transform duration-300">
              <adv.icon size={20} strokeWidth={2} className="text-brand-ink" />
            </div>
            <div>
              <span className="font-mono text-[10px] font-bold tracking-[0.25em] text-brand-ink/40">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-display font-bold text-[15px] sm:text-base text-brand-ink mt-0.5 leading-tight">{adv.label}</h3>
              <p className="text-xs sm:text-[13px] text-brand-ink/55 mt-1 leading-snug">{adv.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
